import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Download, Lock } from "lucide-react";
import { Logo } from "@/components/Nav";
import { fmtUnlock } from "@/components/GiftPanel";
import { api, BACKEND } from "@/lib/api";

function Countdown({ ms }) {
  const s = Math.floor(ms / 1000);
  const parts = [[Math.floor(s / 86400), "days"], [Math.floor((s % 86400) / 3600), "hours"], [Math.floor((s % 3600) / 60), "min"], [s % 60, "sec"]];
  return (
    <div className="mx-auto mt-10 grid max-w-md grid-cols-4 gap-3" data-testid="gift-countdown">
      {parts.map(([v, l]) => (
        <div key={l} className="rounded-2xl border border-[#FAF8F5]/10 bg-[#FAF8F5]/5 py-4 backdrop-blur">
          <p className="font-serif text-3xl tabular-nums sm:text-4xl">{String(v).padStart(2, "0")}</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#FAF8F5]/50">{l}</p>
        </div>
      ))}
    </div>
  );
}

export default function GiftPage() {
  const { token } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = () => api.get(`/gift/${token}`).then((r) => { setG(r.data); document.title = `A caricature for ${r.data.name} · Click, Print & Doodle`; }).catch(() => setErr(true));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!g?.locked) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [g?.locked]);
  const remaining = g?.locked ? new Date(g.unlock_at).getTime() - now : 0;
  useEffect(() => { if (g?.locked && remaining <= 0) load(); }, [remaining <= 0]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grain min-h-screen dark-mesh text-[#FAF8F5]">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
        <div className="[&_*]:text-[#FAF8F5]"><Logo /></div>
        <Link to="/register?next=/caricature" data-testid="gift-make-own-btn" className="btn-primary !px-4 !py-2">Make one <ArrowRight className="h-4 w-4" /></Link>
      </header>
      {err && (
        <main className="mx-auto max-w-xl px-5 py-32 text-center" data-testid="gift-not-found">
          <p className="overline !text-[#9DB8FF]">Link not active</p>
          <h1 className="mt-3 font-serif text-4xl">This gift link has been turned off.</h1>
          <p className="mt-4 text-sm text-[#FAF8F5]/60">Ask the sender for a fresh link — or surprise someone with a caricature of your own.</p>
        </main>
      )}
      {g?.locked && (
        <main className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="gift-locked">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-full border border-[#FAF8F5]/15 bg-[#FAF8F5]/5"><Lock className="h-8 w-8 text-[#9DB8FF]" strokeWidth={1.4} /></div>
          <p className="overline mt-8 !text-[#9DB8FF]">A gift for {g.name}</p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Not yet. Something's coming.</h1>
          <p className="mt-4 text-sm text-[#FAF8F5]/60">This gift unlocks on <span className="text-[#FAF8F5]" data-testid="gift-unlock-time">{fmtUnlock(g.unlock_at)}</span>. Keep this link — come back then.</p>
          <Countdown ms={Math.max(0, remaining)} />
        </main>
      )}
      {g && !g.locked && (
        <main className="mx-auto max-w-5xl px-5 pb-24 pt-6 sm:px-8" data-testid="gift-page">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="order-2 lg:order-1 lg:col-span-5">
              <p className="overline !text-[#9DB8FF]">A gift for you</p>
              <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl" data-testid="gift-name">{g.name}, drawn.</h1>
              {g.dedication && <p className="mt-6 font-serif text-2xl italic leading-snug text-[#FAF8F5]/85" data-testid="gift-dedication">"{g.dedication}"</p>}
              <ul className="mt-6 flex flex-wrap gap-2">
                {g.traits.map((t) => <li key={t} className="rounded-full border border-[#FAF8F5]/20 px-3 py-1 text-xs text-[#FAF8F5]/70">{t}</li>)}
              </ul>
              <a data-testid="gift-download-btn" href={`${BACKEND}${g.download_url}`} className="btn-primary mt-8"><Download className="h-4 w-4" />Download full-size poster</a>
              <p className="mt-3 text-xs text-[#FAF8F5]/50">High resolution, no watermark. Print it, frame it, set it as a wallpaper.</p>
            </div>
            <div className="order-1 lg:order-2 lg:col-span-7">
              <div className="page-shadow mx-auto max-w-[520px] rotate-1 bg-white p-3">
                <img src={`${BACKEND}${g.image_url}`} alt={`Caricature of ${g.name}`} data-testid="gift-image" className="w-full" />
              </div>
            </div>
          </div>
          <div className="mx-auto mt-24 max-w-xl text-center">
            <h2 className="font-serif text-3xl tracking-tight">Know someone who deserves one?</h2>
            <p className="mt-3 text-sm text-[#FAF8F5]/60">One photo and ten quirks — our artist draws them as a classic caricature. Digital ₹399, printed A3 ₹1,000.</p>
            <Link to="/register?next=/caricature" data-testid="gift-footer-cta" className="btn-primary mt-7">Make a Caricature <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </main>
      )}
    </div>
  );
}
